import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { Footer } from '../components/Footer';
import { AuthorByline, AuthorBottomCard, akinPersonSchema } from '../components/AuthorComponents';
import { REVIEWS, ReviewCard, TrustBadge, ReviewsShowcase } from '../components/ReviewComponents';
import { translations } from '../translations';
import './ServicesPage.css';

const SERVICES = [
  {
    row: 'Travel & Tours',
    items: [
      { title: 'Private Istanbul Tours', desc: 'Customized walking tours of Istanbul\'s historic districts with a licensed guide. Topkapi, Hagia Sophia, Sultanahmet, Galata, and beyond.', to: '/private-istanbul-tours', icon: 'fas fa-map-marked-alt' },
      { title: 'Old City Walking Tour', desc: 'Half-day or full-day expert-led exploration of Sultanahmet — Blue Mosque, Hagia Sophia, Hippodrome, Grand Bazaar.', to: '/istanbul-old-city-tour', icon: 'fas fa-mosque' },
      { title: 'Layover Tours', desc: '8-hour standard private tour for transit travelers at IST Airport. From €270 for two people.', to: '/layover-tours-istanbul', icon: 'fas fa-plane-arrival' },
    ]
  },
  {
    row: 'Investment & Long-Term Services',
    items: [
      { title: 'Turkish Citizenship Advisory', desc: 'Citizenship by Investment guidance: process, documents, timeline, property selection. End-to-end advisory.', to: '/tours/turkish-citizenship-investment', icon: 'fas fa-passport' },
      { title: 'Real Estate Consulting', desc: 'Investment property guidance in Istanbul, Bodrum, Antalya. From sourcing to closing, with bilingual contract review.', to: '/real-estate', icon: 'fas fa-building' },
      { title: 'Residence Permit Support', desc: 'Long-term Turkey residency: documentation, application, renewal. Practical guidance for foreigners settling in Turkey.', to: '/contact', icon: 'fas fa-id-card', badge: 'Contact for details' },
    ]
  },
  {
    row: 'Trip Planning & Logistics',
    items: [
      { title: 'Custom Itineraries', desc: 'Multi-day Istanbul or Turkey trip planning. You tell me your interests; I build the plan.', to: '/contact', icon: 'fas fa-route', badge: 'Contact for details' },
      { title: 'Airport Transfers', desc: 'Private vehicle transfers from IST and SAW airports with English-speaking driver.', to: '/contact', icon: 'fas fa-shuttle-van', badge: 'Contact for details' },
      { title: 'Translation & Local Support', desc: 'Document translation, government office accompaniment, business meeting interpretation. Turkish/English/Russian.', to: '/contact', icon: 'fas fa-language', badge: 'Contact for details' },
    ]
  },
  {
    row: 'Specialty Experiences',
    items: [
      { title: 'Bosphorus Cruises', desc: 'Private or shared Bosphorus tours by boat. Sunset cruises, dinner cruises, full-day Asian side combinations.', to: '/tours/bosphorus-cruises', icon: 'fas fa-ship' },
      { title: 'Food & Culture Tours', desc: 'Halal-friendly food tours, market visits, cooking experiences, meyhane evenings with local families.', to: '/tours/food-culture-tours', icon: 'fas fa-utensils' },
      { title: 'Layover Quick Tours', desc: '6-hour express tour for tight layovers (under 8 hours). From €180 solo.', to: '/layover-tours-istanbul', icon: 'fas fa-bolt' },
    ]
  },
];

const FAQS = [
  { q: 'How do I get started?', a: 'Send me a WhatsApp message with what you need — a tour date, a citizenship question, a property inquiry, anything. I respond within 2 hours during business hours. There is no form to fill out, no ticket system, no chatbot. You message me, I reply. From there we figure out timing, pricing, and logistics together. Most clients go from first message to confirmed plan within 24 hours.' },
  { q: 'Do you charge for consultations?', a: 'Initial consultations are free — whether it is a 10-minute WhatsApp chat about your layover options or a 30-minute call about citizenship timelines. I only charge when we move to actual service delivery (a confirmed tour, a property viewing trip, a citizenship filing). I would rather spend 15 minutes helping you figure out if Turkey is right for you than push a service you do not need.' },
  { q: 'What payment methods do you accept?', a: 'For tours: a small deposit via bank transfer or online payment to confirm, with the balance paid in cash (euros, dollars, or Turkish lira) on the day. For consulting and advisory services: bank transfer or Western Union. I do not currently accept credit cards directly, but I can arrange card payment through partner agencies for tour bookings if needed.' },
  { q: 'Are tours licensed and insured?', a: 'I hold a Professional Tour Guide License (Kokart) issued by Turkey\'s Ministry of Culture and Tourism. All tour bookings are facilitated through TÜRSAB-licensed partner agencies, which means full financial guarantees and professional liability insurance. This is not a freelance operation — it is a properly licensed, regulated service with legal consumer protections.' },
  { q: 'What languages can you serve in?', a: 'I am fluent in English and Turkish — these are my primary working languages for all services. I speak conversational Russian (enough for tours and basic consulting discussions) and basic Arabic (greetings, directions, simple conversations). For complex legal or financial discussions in Russian or Arabic, I can arrange a professional interpreter.' },
];

export default function ServicesPage({ language }) {
  const t = translations[language];

  const servicesSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://viaturkey.net/#organization",
        "name": "Viaturkey Investment & Tourism Services Co.",
        "url": "https://viaturkey.net"
      },
      ...SERVICES.flatMap(row => row.items.map(s => ({
        "@type": "Service",
        "name": s.title,
        "description": s.desc,
        "url": `https://viaturkey.net${s.to}`,
        "provider": { "@id": "https://viaturkey.net/#organization" },
        "areaServed": { "@type": "City", "name": "Istanbul" }
      }))),
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://viaturkey.net/" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://viaturkey.net/services" }
        ]
      },
      akinPersonSchema
    ]
  };

  return (
    <div className="svc-page" data-testid="services-page">
      <SEOHead
        title="Viaturkey Services — Tours, Citizenship & Real Estate Consulting"
        description="Licensed tour guide Akın Güzel offers Istanbul tours, Turkish citizenship advisory, and real estate consulting. 17 years experience, 1,000+ international clients, services in English, Russian, Arabic, Turkish."
        canonical="https://viaturkey.net/services"
        schema={servicesSchema}
      />

      {/* Hero */}
      <section className="svc-hero" data-testid="svc-hero">
        <div className="svc-hero-overlay"></div>
        <div className="svc-hero-content">
          <h1>Three Lines of Service. One Licensed Expert.</h1>
          <p>Tours. Citizenship. Real Estate. Whether you're visiting Istanbul for 8 hours or investing for 8 years, you talk to the same person — Akın Güzel, licensed tour guide and travel consultant with 17 years of experience.</p>
          <div className="svc-hero-ctas">
            <a href="https://wa.me/905075337545?text=Hi%20Ak%C4%B1n%2C%20I%20visited%20your%20services%20page%20and%20I%27m%20interested%20in..." target="_blank" rel="noopener noreferrer" className="svc-btn-primary" data-testid="svc-hero-cta">
              <i className="fab fa-whatsapp"></i> Plan My Trip
            </a>
            <a href="#services-grid" className="svc-btn-secondary" data-testid="svc-scroll-cta">
              See How I Can Help <i className="fas fa-arrow-down"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="svc-values" data-testid="svc-values">
        <div className="svc-values-grid">
          <div className="svc-value-card">
            <i className="fas fa-id-badge"></i>
            <h3>Licensed</h3>
            <p>Professional Tour Guide License (Kokart) issued by Turkey's Ministry of Culture and Tourism. Erciyes University, Faculty of Tourism graduate.</p>
          </div>
          <div className="svc-value-card">
            <i className="fas fa-user-check"></i>
            <h3>Independent</h3>
            <p>You talk directly to me — not a call center, not a chatbot, not a junior agent. Every conversation, every plan, every tour: directly with Akın.</p>
          </div>
          <div className="svc-value-card">
            <i className="fas fa-globe"></i>
            <h3>Multi-Lingual</h3>
            <p>Fluent in English and Turkish. Conversational Russian. Basic Arabic. 17 years of communicating with international visitors from 30+ countries.</p>
          </div>
          <div className="svc-value-card">
            <i className="fas fa-award"></i>
            <h3>Experienced</h3>
            <p>1,000+ international clients served since 2008. Viaturkey founded 2016. Specialized in US, GCC, and Russian markets.</p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="svc-intro" data-testid="svc-intro">
        <div className="svc-container">
          <AuthorByline publishDate="June 2026" updateDate="June 2026" />
          <h2>What I Do for International Visitors</h2>
          <p>
            Viaturkey exists because international visitors to Turkey deserve three things: an honest guide, a clear plan, and one person who actually picks up the phone.
          </p>
          <p>
            I'm Akın Güzel — a licensed tour guide based in Istanbul. Over 17 years, I've helped more than 1,000 international travelers and investors navigate Turkey, from <Link to="/layover-tours-istanbul" data-testid="svc-layover-link-intro">Istanbul layover tours</Link> to multi-year citizenship processes.
          </p>
          <p>
            What I offer isn't packaged. It's customized. Whether you need a walking tour of Sultanahmet between two flights, or you're planning to relocate, invest in property, and apply for Turkish citizenship — the conversation starts the same way: a direct message to me.
          </p>
          <p>
            My services are organized into three categories: short-term travel experiences (tours, transfers, itineraries), medium-term planning (custom trips, translation, support), and long-term advisory (citizenship, real estate, residence permits).
          </p>
          <p>
            For tours requiring full agency licensing, I work with TÜRSAB-licensed partner agencies. For consulting and advisory services, you work directly with me.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="svc-grid-section" id="services-grid" data-testid="svc-grid">
        <div className="svc-container">
          {SERVICES.map((row, ri) => (
            <div key={ri} className="svc-row">
              <h2 className="svc-row-title">{row.row}</h2>
              <div className="svc-cards">
                {row.items.map((s, si) => (
                  <Link key={si} to={s.to} className="svc-card" data-testid={`svc-card-${ri}-${si}`}>
                    <div className="svc-card-icon"><i className={s.icon}></i></div>
                    <h3>{s.title}</h3>
                    {s.badge && <span className="svc-card-badge">{s.badge}</span>}
                    <p>{s.desc}</p>
                    <span className="svc-card-link">Learn more <i className="fas fa-arrow-right"></i></span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Social Proof */}
      <ReviewsShowcase />

      {/* FAQ */}
      <section className="svc-faq" data-testid="svc-faq">
        <div className="svc-container">
          <h2>Common Questions</h2>
          <div className="svc-faq-list">
            {FAQS.map((f, i) => (
              <details key={i} className="svc-faq-item" data-testid={`svc-faq-${i}`}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
          <div className="svc-faq-more">
            <Link to="/faq" data-testid="svc-faq-all">See All FAQs <i className="fas fa-arrow-right"></i></Link>
          </div>
        </div>
      </section>

      {/* Author Bottom Card */}
      <section className="svc-container" style={{maxWidth: '800px', margin: '0 auto', padding: '0 1rem 2rem'}}>
        <AuthorBottomCard />
      </section>

      {/* Final CTA */}
      <section className="svc-final-cta" data-testid="svc-final-cta">
        <div className="svc-container">
          <h2>Let's Plan Your Turkey Experience</h2>
          <p>Whether it's one of our <Link to="/layover-tours-istanbul" data-testid="svc-layover-link-cta">Istanbul layover tours</Link> tomorrow or next year's citizenship application — I'm a WhatsApp message away.</p>
          <div className="svc-cta-buttons">
            <a href="https://wa.me/905075337545?text=Hi%20Ak%C4%B1n%2C%20I%20need%20help%20with..." target="_blank" rel="noopener noreferrer" className="svc-btn-primary" data-testid="svc-final-whatsapp">
              <i className="fab fa-whatsapp"></i> Start a WhatsApp Conversation
            </a>
            <Link to="/contact" className="svc-btn-outline" data-testid="svc-final-contact">
              Or schedule a call <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer t={t} />
    </div>
  );
}
